import React, {
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Loader, Modal } from 'ncoded-component-library';
import api from 'api';
import DownloadIcon from 'icons/Download.icon';
import { PdfViewer } from 'components/PdfViewer';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { useTranslation } from 'react-i18next';
import {
  extractNodeWatermarkSettings,
  getIsOwner,
  getUserFullName,
  isDwg,
  isEml,
  isImage,
  isOfficeFileType,
  isPdf,
  isVideo,
} from 'utils';
import useCloseOnEscape from 'hooks/useCloseOnEscape';
import useFilePreviewTrackTime from 'hooks/useFilePreviewTrackTime';
import useDisableRightClick from 'hooks/useDisableRightClick';
import { SoftSignal } from 'models/Lead';
import SoftSignals from './components/SoftSignals';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { BUYING_PORTFOLIO_ROUTE } from 'data/global';
import { useNavigate } from 'react-router-dom';
import useMixpanelDocumentEvents from 'hooks/useMixpanelDocumentEvents';
import OfficeFilePreview from 'components/OfficeFilePreview';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { DataRoomFileBufferPreview } from 'models/File';
import useStartDownloadingFile from 'hooks/useStartDownloadingFile';
import CannotPreviewInBrowser from 'components/CannotPreviewInBrowser';
import AutodeskViewer from 'components/AutodeskViewer';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import { NeedToSignNdaModalRef } from 'components/NeedToSignNdaModal/NeedToSignNdaModal.component';
import NeedToSignNdaModal from 'components/NeedToSignNdaModal';
import ShareableLinkNdaProcessing from 'router/subrouters/ShareableLink/components/ShareableLinkNdaProcessing';
import ShareableLinkNdaCountersign from 'router/subrouters/ShareableLink/components/ShareableLinkNdaCountersign';

import '@react-pdf-viewer/core/lib/styles/index.css';
import './DataroomNodeViewModal.styles.scss';
import './DataroomNodeViewModal.styles.responsive.scss';

const getCannotPreviewInBrowser = ({
  type,
  urn,
  isAutodeskProccessed,
}: DataRoomFileBufferPreview) =>
  isEml(type) || (isDwg(type) && (!urn || !isAutodeskProccessed));

export type SoftSignalsDetails = {
  displayBanner?: boolean;
  lastSoftSignal?: SoftSignal;
};

type DataroomNodeViewModalProps = {
  className?: string;
  node: DataRoomNode;
  isCurrentlyActiveNode?: boolean;
  dataRoomName?: string;
  ownerId: string;
  userSide: QAndASides;
};

const DataroomNodeViewModal: ForwardRefRenderFunction<
  ModalRef,
  DataroomNodeViewModalProps
> = (props, ref) => {
  const {
    className,
    node,
    dataRoomName,
    ownerId,
    userSide,
    isCurrentlyActiveNode = true,
  } = props;

  const {
    _id: nodeId,
    name: documentName,
    dataRoomId,
    canDownload,
    version,
    dataRoom,
    isBuyerFile,
    userId,
  } = node || {};

  const { t } = useTranslation();

  const classes = classNames('data-room-node-view-modal', className);

  const [isUrlFetching, setIsUrlFetching] = useState(false);
  const [data, setData] = useState<DataRoomFileBufferPreview>();
  const [Fallback, setFallback] = useState<ReactNode>();

  const {
    currentUser: { _id: currentUserId, firstName, lastName, email },
  } = useContext(CurrentUserContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const navigate = useNavigate();

  const [modal, modalRef] = useCallbackRef<ModalRef>();
  const [needToSignNdaModal, needToSignNdaModalRef] =
    useCallbackRef<NeedToSignNdaModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const isModalOpen = modal?.openState;

  const { url, type, autodeskToken, urn, displayBanner, lastSoftSignal } =
    data || {};

  const userFullName = useMemo(
    () => getUserFullName(firstName, lastName),
    [firstName, lastName],
  );

  useDisableRightClick(isModalOpen);

  const startDownloadingFile = useStartDownloadingFile();

  const RenderTitle = useMemo(
    () =>
      canDownload && (
        <Button
          variant="outline"
          className="data-room-node-view-modal__download"
          icon={DownloadIcon}
          onClick={() => startDownloadingFile(nodeId)}
        >
          {t('download')}
        </Button>
      ),
    [canDownload, nodeId, startDownloadingFile, t],
  );

  const fetchUrl = useCallback(async () => {
    try {
      setIsUrlFetching(true);

      const { data } = await api.dataRoom.nodes.getFileBufferPreview(
        nodeId,
        isCurrentlyActiveNode,
      );

      if (!data) {
        modal.close();
        return;
      }

      const {
        requiresNdaSign,
        shareableLinkId,
        isWaitingNdaConfirmation,
        isWaitingCounterSign,
      } = data;

      if (isWaitingNdaConfirmation) {
        setFallback(<ShareableLinkNdaProcessing />);
        return;
      }

      if (isWaitingCounterSign) {
        setFallback(<ShareableLinkNdaCountersign />);
        return;
      }

      if (requiresNdaSign) {
        modal.close();
        needToSignNdaModal.open(
          api.shareableLinks.getNDADocusignUrl(shareableLinkId, {
            redirectUrl: window.location.href,
          }),
        );
        return;
      }

      if (getCannotPreviewInBrowser(data)) {
        startDownloadingFile(nodeId);
        setFallback(<CannotPreviewInBrowser />);
        return;
      }

      setData(data);
    } catch (e) {
      popServerError(e);
      modal?.close();
    } finally {
      setIsUrlFetching(false);
    }
  }, [
    nodeId,
    isCurrentlyActiveNode,
    startDownloadingFile,
    modal,
    needToSignNdaModal,
    popServerError,
  ]);

  const sendReport = useCallback(
    (time: number, currentPage: number) => {
      try {
        api.dataRoom.reports.trackTime(
          dataRoomId,
          nodeId,
          currentPage,
          time,
          version || 1,
        );
      } catch (e) {
        return;
      }
    },
    [dataRoomId, nodeId, version],
  );

  const { onDocumentViewed } = useMixpanelDocumentEvents({
    documentName,
    dataRoomName,
    ownerId,
    userSide,
  });

  const { isLoaded, onDocumentLoad, onPageChange } = useFilePreviewTrackTime({
    isModalOpen,
    sendReport,
    onDocumentViewed,
  });

  const onSoftSignalSuccess = useCallback(
    (lastSoftSignal: SoftSignal, withdrawInterest?: boolean) => {
      setData((prev) => ({ ...prev, lastSoftSignal }));

      if (withdrawInterest) {
        popSuccess(t('interestWithdrawnSuccessfully'));
        modal?.close();
        setTimeout(() => navigate(BUYING_PORTFOLIO_ROUTE), 2000);
      }
    },
    [modal, navigate, popSuccess, t],
  );

  const renderFooter = useMemo(
    () =>
      isLoaded &&
      displayBanner &&
      !isBuyerFile &&
      !getIsOwner(userId, currentUserId),
    [displayBanner, isBuyerFile, isLoaded, userId, currentUserId],
  );

  const RenderFooter = useMemo(
    () =>
      renderFooter && (
        <SoftSignals
          lastSoftSignal={lastSoftSignal}
          dataRoomNodeId={nodeId}
          dataRoomId={dataRoomId}
          dealRoomId={dataRoom?.dealRoomId}
          onSuccess={onSoftSignalSuccess}
        />
      ),
    [
      renderFooter,
      lastSoftSignal,
      dataRoomId,
      dataRoom,
      nodeId,
      onSoftSignalSuccess,
    ],
  );

  // fetch when open, reset url on close because we want to track view count
  useEffect(() => {
    if (!isModalOpen && (data || Fallback)) {
      setData(null);
      setFallback(null);
    }

    if (isModalOpen && !isUrlFetching && !data && !Fallback) fetchUrl();
  }, [isUrlFetching, url, isModalOpen, fetchUrl, data, Fallback]);

  useCloseOnEscape(modal?.close, { capture: true, enabled: isModalOpen });

  return (
    <>
      <Modal
        ref={modalRef}
        className={classes}
        title={RenderTitle}
        footer={RenderFooter}
        lockScroll={false}
      >
        {isUrlFetching && <Loader />}

        {Fallback}

        {isPdf(type) && (
          <PdfViewer
            url={url}
            onPageChange={onPageChange}
            onDocumentLoad={onDocumentLoad}
          />
        )}

        {isOfficeFileType(type) && (
          <OfficeFilePreview
            data={data}
            email={email}
            fullName={userFullName}
            watermarkSettings={extractNodeWatermarkSettings(node)}
            onDocumentLoad={onDocumentLoad}
          />
        )}

        {isDwg(type) && (
          <AutodeskViewer
            urn={urn}
            accessToken={autodeskToken}
            email={email}
            fullName={userFullName}
            ipAddress={data?.ipAddress}
            watermarkSettings={extractNodeWatermarkSettings(node)}
            onDocumentLoad={onDocumentLoad}
          />
        )}

        {isImage(type) && (
          <img
            alt={documentName}
            src={url}
            onLoadedData={() => onDocumentLoad()}
          />
        )}

        {isVideo(type) && (
          <video
            src={url}
            controls
            controlsList="nodownload"
            onLoadedData={() => onDocumentLoad()}
          />
        )}
      </Modal>

      <NeedToSignNdaModal
        ref={needToSignNdaModalRef}
        title={t('signNdaToAccessThisFile')}
      >
        <p>{t('thisFileRequiresSignedNDABeforeAccessIsGranted')}</p>
      </NeedToSignNdaModal>
    </>
  );
};

export default forwardRef(DataroomNodeViewModal);
